import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import axios from 'axios';

import Login from './components/Login';
import SetPassword from './components/SetPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import Analytics from './components/Analytics';
import TenantManager from './components/TenantManager';
import DocumentManager from './components/DocumentManager';
import ChatPreview from './components/ChatPreview';
import UserManager from './components/UserManager';
import UserProfile from './components/UserProfile';
import DataSources from './components/DataSources';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';

const isLocalDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const finalApiUrl = isLocalDevelopment ? (process.env.REACT_APP_API_URL || 'http://localhost:8000') : '';
axios.defaults.baseURL = finalApiUrl;
if (!isLocalDevelopment) {
  axios.defaults.headers.common['X-Forwarded-Proto'] = 'https';
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mfaToken, setMfaToken] = useState(null);

  const path = window.location.pathname;
  const isPublicRoute = path === '/set-password' || path === '/reset-password';

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await axios.get('/api/auth/me');
        setUser(response.data);
        setIsAuthenticated(true);
      } catch {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
      }
    }
    setLoading(false);
  };

  const login = async (credentials) => {
    try {
      const response = await axios.post('/api/auth/login', credentials);
      if (response.data.mfa_required) {
        setMfaToken(response.data.mfa_token);
        return { success: true };
      }
      return { success: false, error: 'Unexpected response' };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const verifyMfa = async ({ mfa_token, code }) => {
    try {
      const response = await axios.post('/api/auth/verify-mfa', { mfa_token, code });
      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(user);
      setMfaToken(null);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Verification failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setMfaToken(null);
    setIsAuthenticated(false);
  };

  if (loading) return <div className="loading"><div>Loading...</div></div>;

  if (isPublicRoute) {
    return (
      <>
        {path === '/set-password' && <SetPassword />}
        {path === '/reset-password' && <ResetPassword />}
        <Toaster position="top-right" />
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <Login onLogin={login} onVerifyMfa={verifyMfa} mfaToken={mfaToken} />
        <Toaster position="top-right" />
      </>
    );
  }

  return (
    <Router>
      <div className="app">
        <Navbar user={user} onLogout={logout} />
        <div className="d-flex">
          <Sidebar user={user} />
          <div className="main-content">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/documents" element={<DocumentManager />} />
              <Route path="/tenants" element={<TenantManager />} />
              <Route path="/preview" element={<ChatPreview />} />
              {user?.role === 'superadmin' && (
                <Route path="/users" element={<UserManager />} />
              )}
              <Route path="/data-sources" element={<DataSources />} />
              <Route path="/profile" element={<UserProfile user={user} onUserUpdated={setUser} />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </div>
        <Toaster position="top-right" />
      </div>
    </Router>
  );
}

export default App;
